<script>
import {
  isEmpty,
  isUndefined,
  isNull,
  debounce,
  without
} from 'lodash'
import { ref, computed, watch, onMounted, onBeforeUnmount } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'

import {
  Filters,
  Sidebar,
  GridControls,
  OrganizeContent,
  FilterPills
} from '@/components/Organize'
import CollectionModal from '@/components/Assets/CollectionModal.vue'
import AddToCollectionModal from '@/components/Assets/AddToCollectionModal.vue'
import RemoveFromCollectionModal from '@/components/Assets/RemoveFromCollectionModal.vue'
import store from '@/store'
import router from '@/router'
import { useCollections, useAssetPropertyOptions } from '@/features'

export default {
  name: 'Organize',

  components: {
    Filters,
    Sidebar,
    GridControls,
    OrganizeContent,
    CollectionModal,
    AddToCollectionModal,
    RemoveFromCollectionModal,
    FilterPills
  },

  setup () {
    const toast = useToast()
    const brandId = store.state.brand.settings.id
    const collections = useCollections()
    const assetPropertyOptions = useAssetPropertyOptions()

    const showAddToCollectionModal = ref(false)
    const showRemoveFromCollectionModal = ref(false)
    const showCreateCollectionModal = ref(false)
    const view = ref('grid')

    const assetsLoading = ref(true)
    const loadingNewPage = ref(false)
    const loading = ref(false)

    const selectedAssetIndexes = ref([])
    const topicSearchTerm = ref('')
    const topicSort = ref('num')
    const topicOrder = ref('desc')
    const keywordSearchTerm = ref('')
    const keywordSort = ref('num')
    const keywordOrder = ref('desc')
    const propertySearchTerm = ref('')
    const propertySort = ref('asset_count')
    const propertyOrder = ref('desc')

    const page = computed(() => store.state.organize.page)
    const totalAssetCount = computed(() => store.state.organize.totalAssets)
    const assets = computed(() => store.state.organize.assets)
    const searchQueryParams = computed(() => store.getters['organize/searchQueryParams'])
    const filters = computed(() => store.state.organize.filters)
    const sort = computed(() => store.state.organize.sort)
    const domainOptions = computed(() => store.state.organize.dynamicOptions.domains)
    const languageOptions = computed(() => store.state.organize.dynamicOptions.languages)
    const topicOptions = computed(() => store.getters['organize/topicOptionsAsTree'])
    const keywordOptions = computed(() => store.state.organize.dynamicOptions.keywords)
    const collectionOptions = computed(() =>
      collections.list.value
        .map(c => ({
          label: c.name,
          value: String(c.id),
          assets: c.asset_set
        }))
    )
    const collectionOptionsForAddToCollectionModal = computed(() =>
      collectionOptions.value
        .map(obj => ({
          name: obj.label,
          id: obj.value,
          assets: obj.assets
        }))
    )
    const selectedCollection = computed(() => {
      if (isEmpty(filters.value.collection)) {
        return null
      }

      return collectionOptions.value
        .find(item => item.value === filters.value.collection)
    })
    const selectedAssetIds = computed(() =>
      selectedAssetIndexes.value
        .filter(idx => !isUndefined(assets.value[idx]))
        .map(idx => assets.value[idx].page_url_id)
    )
    const hasMoreAssets = computed(() => assets.value.length < totalAssetCount.value)
    const topicsLoading = computed(() => store.getters['organize/optionLoading']('topics'))
    const keywordsLoading = computed(() => store.getters['organize/optionLoading']('keywords'))

    const onSortKeywords = type => {
      if (!isNull(keywordSort.value) && type === keywordSort.value) {
        keywordSort.value = type
        keywordOrder.value = keywordOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        keywordSort.value = type
      }
      onKeywordsSearch()
    }
    const onSortTopics = type => {
      if (!isNull(topicSort.value) && type === topicSort.value) {
        topicSort.value = type
        topicOrder.value = topicOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        topicSort.value = type
      }
      onTopicSearch()
    }
    const onSortProperties = type => {
      if (!isNull(propertySort.value) && type === propertySort.value) {
        propertySort.value = type
        propertyOrder.value = propertyOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        propertySort.value = type
      }
      onPropertySearch()
    }
    const onAssetTablePageChange = newPage => {
      assetsLoading.value = true
      loadingNewPage.value = true
      store.commit('organize/setPage', newPage)
      updateClientUrl()
        .then(() => getAssets())
        .finally(() => {
          assetsLoading.value = false
          loadingNewPage.value = false
        })
    }
    const onSelectAllAssets = () => {
      selectedAssetIndexes.value = assets.value.map((_asset, idx) => idx)
    }
    const onUnselectAllAssets = () => { selectedAssetIndexes.value = [] }
    const getAssets = () => store.dispatch('organize/getAssets', searchQueryParams.value)
    const getMoreAssets = () => store.dispatch('organize/getMoreAssets', searchQueryParams.value)
    const onFiltersChange = ({ key, value }) => {
      assetsLoading.value = true
      store.commit('organize/setFilter', { key, value })
      store.commit('organize/setPage', 1)
      updateClientUrl()
        .then(() => {
          return getAssets()
        })
        .then(() => {
          return updateSidebarValues()
        })
        .finally(() => {
          assetsLoading.value = false
          selectedAssetIndexes.value = []
        })
    }
    const updateSidebarValues = () => {
      return new Promise((resolve, reject) => {
        Promise.all([
          loadKeywordOptions(),
          loadTopicOptions(),
          loadAssetPropertyOptions()
        ])
        .then(() => resolve())
        .catch(err => reject(err))
      })
    }
    const loadKeywordOptions = () => {
      return store.dispatch('organize/loadKeywordOptions', {
        brand_ids: filters.value.brandIds,
        sort: keywordSort.value,
        order: keywordOrder.value,
        search: keywordSearchTerm.value || null,
        domain: filters.value.domain || null,
        language: filters.value.language || null,
        data_providers: filters.value.assetSources,
        created_date_range: searchQueryParams.value.created_date_range,
        distribution: filters.value.distribution,
        phrase: filters.value.keyword
      })
    }
    const loadTopicOptions = () => {
      return store.dispatch('organize/loadTopicOptions', {
        brand_ids: filters.value.brandIds,
        order_by: topicSort.value,
        order_direction: topicOrder.value,
        search: topicSearchTerm.value || null,
        domain: filters.value.domain || null,
        language: filters.value.language || null,
        data_providers: filters.value.assetSources,
        created_date_range: searchQueryParams.value.created_date_range,
        distribution: filters.value.distribution,
        phrase: filters.value.keyword
      })
    }
    const loadAssetPropertyOptions = async () => {
      await assetPropertyOptions.loadOptions({
        brandId,
        phrase: propertySearchTerm.value,
        domain: filters.value.domain,
        language: filters.value.language,
        dataProviders: filters.value.assetSources,
        createdDateRange: searchQueryParams.value.created_date_range,
        orderBy: propertySort.value,
        orderDirection: propertyOrder.value,
        distribution: filters.value.distribution,
        topPhrase: filters.value.keyword
      })
    }
    const updateClientUrl = () => store
      .dispatch('organize/updateBrowserUrl', store.getters['organize/browserQueryParams'])
    const onFiltersBulkChange = payload => {
      assetsLoading.value = true
      store.commit('organize/setFiltersFromObject', payload)
      store.commit('organize/setPage', 1)
      updateClientUrl()
        .then(() => {
          return getAssets()
        })
        .then(() => {
          return updateSidebarValues()
        })
        .finally(() => {
          assetsLoading.value = false
          selectedAssetIndexes.value = []
        })
    }
    const onSortChange = ({ key, value }) => {
      assetsLoading.value = true
      store.commit('organize/setSort', { key, value })
      store.commit('organize/setPage', 1)
      updateClientUrl()
        .then(() => {
          return getAssets()
        })
        .finally(() => {
          assetsLoading.value = false
          selectedAssetIndexes.value = []
        })
    }
    const onCollectionModalCreate = async formData => {
      loading.value = true
      showCreateCollectionModal.value = false
      try {
        await collections.create(
          formData.name,
          formData.description,
          brandId,
          selectedAssetIds.value
        )
        toast.success('Collection created')
      } catch (error) {
        console.error(error)
        toast.error('Error creating collection')
      } finally {
        selectedAssetIndexes.value = []
        await collections.getList(brandId)
        loading.value = false
      }
    }
    const onAddToCollection = async collection => {
      loading.value = true
      showAddToCollectionModal.value = false
      try {
        await collections.addToCollection(collection.id, collection.assets, selectedAssetIds.value)
        selectedAssetIndexes.value = []
        toast.success('Collection updated')
      } catch (error) {
        console.log(error)
        toast.error('Error updating collection')
      } finally {
        await collections.getList(brandId)
        loading.value = false
      }
    }
    const onRemoveFromCollection = async () => {
      loading.value = true
      showRemoveFromCollectionModal.value = false
      try {
        await collections.removeFromCollection(
          selectedCollection.value.value,
          selectedCollection.value.assets,
          selectedAssetIds.value
        )
        toast.success('Collection updated')
        await getAssets()
      } catch (error) {
        console.error(error)
        toast.error('Error updating collection')
      } finally {
        selectedAssetIndexes.value = []
        loading.value = false
      }
    }
    const onContentNextPage = async () => {
      assetsLoading.value = true
      loadingNewPage.value = true
      store.commit('organize/setPage', page.value + 1)
      await updateClientUrl()
      await getMoreAssets()
      assetsLoading.value = false
      loadingNewPage.value = false
    }
    const onKeywordsSearch = debounce(function () {
      return loadKeywordOptions()
    }, 300)
    const onPropertySearch = debounce(async function () {
      await loadAssetPropertyOptions()
    }, 300)
    const onTopicSearch = debounce(function () {
      return loadTopicOptions()
    })
    const onFilterPillClear = async ({ key, value }) => {
      let filterValue

      if (Array.isArray(filters.value[key])) {
        filterValue = without(filters.value[key], value)
      } else {
        filterValue = store.getters['organize/defaultFilter'](key)
      }

      assetsLoading.value = true
      store.commit('organize/setFilter', { key, value: filterValue })
      store.commit('organize/setPage', 1)
      await updateClientUrl()
      await getAssets()
      assetsLoading.value = false
      selectedAssetIndexes.value = []
    }
    const onFilterPillClearAll = () => {
      const payload = {
        keywords: store.getters['organize/defaultFilter']('keywords'),
        topics: store.getters['organize/defaultFilter']('topics'),
        collection: store.getters['organize/defaultFilter']('collection'),
        customProperties: store.getters['organize/defaultFilter']('customProperties')
      }

      onFiltersBulkChange(payload)
    }
    const initState = async () => {
      if (!isEmpty(router.currentRoute.query)) {
        store.commit('organize/setFiltersFromObject', router.currentRoute.query)
        store.commit('organize/setSortFromObject', router.currentRoute.query.sort || {})
      } else {
        await store.dispatch('organize/resetFilters', brandId)
      }

      if (!router.currentRoute.query.brandIds) {
        store.commit('organize/setFilter', { key: 'brandIds', value: [ String(brandId) ] })
      }
    }
    const loadOptions = async () => {
      await Promise.all([
        store.dispatch('organize/loadDomainOptions', brandId),
        store.dispatch('organize/loadLanguageOptions', brandId),
        loadKeywordOptions(),
        loadAssetPropertyOptions(),
        collections.getList(brandId),
        loadTopicOptions()
      ])
    }

    watch(view, async () => {
      assetsLoading.value = true
      store.commit('organize/setPage', 1)
      await updateClientUrl()
      await getAssets()
      assetsLoading.value = false
      selectedAssetIndexes.value = []
    })
    watch(keywordSearchTerm, onKeywordsSearch)
    watch(propertySearchTerm, onPropertySearch)
    watch(topicSearchTerm, onTopicSearch)

    onMounted(async () => {
      await initState()
      await getAssets()
      assetsLoading.value = false
      await loadOptions()
    })
    onBeforeUnmount(() => { store.commit('organize/resetStateToDefault') })

    return {
      filters,
      domainOptions,
      languageOptions,
      topicOptions,
      keywordOptions,
      propertyOptions: assetPropertyOptions.optionsAsTree,
      collectionOptions,
      topicsLoading,
      keywordsLoading,
      propertiesLoading: assetPropertyOptions.optionsLoading,
      collectionsLoading: collections.listLoading,
      topicSearchTerm,
      keywordSearchTerm,
      propertySearchTerm,
      topicSort,
      keywordSort,
      propertySort,
      sort,
      view,
      showCreateCollectionModal,
      showAddToCollectionModal,
      showRemoveFromCollectionModal,
      assets,
      hasMoreAssets,
      assetsLoading,
      loadingNewPage,
      page,
      totalAssetCount,
      collectionOptionsForAddToCollectionModal,
      selectedCollection,
      loading,
      selectedAssetIndexes,

      onFiltersChange,
      onFiltersBulkChange,
      onSortKeywords,
      onSortTopics,
      onSortProperties,
      onSortChange,
      onSelectAllAssets,
      onUnselectAllAssets,
      onFilterPillClear,
      onFilterPillClearAll,
      onContentNextPage,
      onAssetTablePageChange,
      onCollectionModalCreate,
      onAddToCollection,
      onRemoveFromCollection
    }
  }
}
</script>

<template>
  <div id="organizeView">
    <div class="container">
      <section class="organizeView-filters">
        <filters :filters="filters"
          :domainOptions="domainOptions"
          :languageOptions="languageOptions"
          @change="onFiltersChange"
          @bulkChange="onFiltersBulkChange"
        />
      </section>

      <section class="organizeView-sidebar">
        <sidebar :topicOptions="topicOptions"
          :selectedTopics="filters.topics"
          :keywordOptions="keywordOptions"
          :selectedKeywords="filters.keywords"
          :propertyOptions="propertyOptions"
          :selectedProperties="filters.customProperties"
          :collectionOptions="collectionOptions"
          :selectedCollection="filters.collection"
          :topicsLoading="topicsLoading"
          :keywordsLoading="keywordsLoading"
          :propertiesLoading="propertiesLoading"
          :collectionsLoading="collectionsLoading"
          :topicSearchTerm="topicSearchTerm"
          :keywordSearchTerm="keywordSearchTerm"
          :propertySearchTerm="propertySearchTerm"
          :topicSort="topicSort"
          :keywordSort="keywordSort"
          :propertySort="propertySort"
          @change="onFiltersChange"
          @topic-search-term-change="value => topicSearchTerm = value"
          @keyword-search-term-change="value => keywordSearchTerm = value"
          @property-search-term-change="value => propertySearchTerm = value"
          @sort-keywords="onSortKeywords"
          @sort-topics="onSortTopics"
          @sort-properties="onSortProperties"
        />
      </section>

      <section class="organizeView-gridControls">
        <grid-controls :sort="sort"
          :selectedAssetIndexes="selectedAssetIndexes"
          :selectedCollectionId="filters.collection"
          :view="view"
          @changeSort="onSortChange"
          @create-collection="showCreateCollectionModal = true"
          @add-to-collection="showAddToCollectionModal = true"
          @remove-from-collection="showRemoveFromCollectionModal = true"
          @select-all="onSelectAllAssets"
          @unselect-all="onUnselectAllAssets"
          @change-view="value => view = value"
        />
      </section>

      <section class="organizeView-filterPills">
        <filter-pills :filters="filters"
          @clear="onFilterPillClear"
          @clear-all="onFilterPillClearAll"
        ></filter-pills>
      </section>

      <section class="organizeView-content">
        <organize-content :assets="assets"
          :selectedAssetIndexes="selectedAssetIndexes"
          :hasMoreAssets="hasMoreAssets"
          :assetsLoading="assetsLoading"
          :loadingNewPage="loadingNewPage"
          :view="view"
          :page="page"
          :totalAssetCount="totalAssetCount"
          @selectedAssetIndexesChanged="value => selectedAssetIndexes = value"
          @next-page="onContentNextPage"
          @page-change="onAssetTablePageChange"
        />
      </section>
    </div>

    <collection-modal :showModal.sync="showCreateCollectionModal"
      :selectedAssetIndexes="selectedAssetIndexes"
      @create="onCollectionModalCreate"
    ></collection-modal>

    <add-to-collection-modal :showModal.sync="showAddToCollectionModal"
      :collectionOptions="collectionOptionsForAddToCollectionModal"
      :selectedAssetIndexes="selectedAssetIndexes"
      @submit="onAddToCollection"
    ></add-to-collection-modal>

    <remove-from-collection-modal :showModal.sync="showRemoveFromCollectionModal"
      :collectionName="selectedCollection ? selectedCollection.name || selectedCollection.label : ''"
      :selectedAssetCount="selectedAssetIndexes.length"
      @submit="onRemoveFromCollection"
    ></remove-from-collection-modal>
  </div>
</template>

<style lang="scss">
#organizeView {
  padding-bottom: 40px;

  .container {
    margin: 0 auto;
    box-sizing: border-box;
    overflow: hidden;
    display: grid;
    grid-template-columns: auto 210px 975px auto;
    grid-template-rows: auto 46px auto 1fr;

    section {
      box-sizing: border-box;
    }
  }
}

.organizeView {
  &-filters {
    grid-column: 1 / span 4;
    grid-row: 1 / span 1;
  }

  &-sidebar {
    grid-column: 2 / span 1;
    grid-row: 2 / span 3;
  }

  &-gridControls {
    grid-column: 3 / span 1;
    grid-row: 2 /span 1;
  }

  &-filterPills {
    grid-column: 3 / span 1;
    grid-row: 3 /  span 1;
    padding-left: 20px;
    padding-bottom: 10px;
  }

  &-content {
    grid-column: 3 / span 1;
    grid-row: 4 / span 1;
  }
}
</style>
